import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../context/AuthProvider";
import { motion } from "framer-motion";
import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from "@headlessui/react";
import { ExclamationTriangleIcon, QuestionMarkCircleIcon, AtSymbolIcon } from "@heroicons/react/24/outline";
import background from "../assets/bateau-bg.jpg";
import logo from "../assets/logo-nanthiat.png";

export default function Login() {
  const { user, login } = useAuth();
  const [Email, setEmail] = useState("");
  const [Password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [open, setOpen] = useState(false);
  const [openForgotPassword, setOpenForgotPassword] = useState(false);
  const navigate = useNavigate();

  // 🔹 Redirection si l'utilisateur est déjà connecté
  useEffect(() => {
    if (user) {
      navigate("/dashboard");
    }
  }, [user, navigate]);

  // 🔹 Fonction de connexion
  const handleLogin = async (e) => {
    e.preventDefault();

    if (!Email || !/^[\w-]+(\.[\w-]+)*@([\w-]+\.)+[a-zA-Z]{2,7}$/.test(Email)) {
        setError("Veuillez entrer un email valide.");
        setOpen(true);
        return;
    }

    let EmailToSend = Email;
    if (typeof EmailToSend === "object" && EmailToSend.Email) {
        console.warn("⚠️ Correction: Email était un objet, extraction de Email.Email");
        EmailToSend = EmailToSend.Email;
    }

    console.log("📌 `handleLogin()` - Envoi Email et Password :", { EmailToSend, Password });

    // 👉 Ici, URL dynamique selon environnement
    const BACKEND_URL =
      window.location.hostname === "localhost"
          ? process.env.REACT_APP_BACKEND_URL_LOCAL + "/api"
          : process.env.REACT_APP_BACKEND_URL_PROD + "/api";

    try {
        const res = await axios.post(`${BACKEND_URL}/utilisateurs/login`, { Email: EmailToSend, Password });
        console.log("✅ Connexion réussie :", res.data);

        localStorage.setItem("token", res.data.token);
        localStorage.setItem("userEmail", res.data.utilisateur.Email);

        console.log("📌 Token stocké dans `localStorage` :", localStorage.getItem("token"));
        console.log("📌 Email stocké dans `localStorage` :", localStorage.getItem("userEmail"));

        if (!Password) {
          console.error("❌ ERREUR: `handleLogin()` essaie d'appeler `login()` sans mot de passe !");
          return;
        }
        login(res.data.utilisateur, Password);
        navigate("/dashboard");
    } catch (err) {
        console.error("❌ Erreur de connexion :", err.response?.data || err);
        setError("Email ou mot de passe incorrect.");
        setOpen(true);
    }
  };

  // 🔹 Tentative de connexion en récupérant l'email depuis localStorage
  useEffect(() => {
    const emailFromStorage = localStorage.getItem("userEmail");

    if (emailFromStorage) {
        setEmail(emailFromStorage); // Si l'email est stocké, pré-remplir le champ
    }
  }, []);

  return (
    <div className="flex h-screen">
      {/* Partie gauche avec animation */}
      <motion.div
        initial={{ opacity: 0, x: -100 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 1 }}
        className="w-1/2 flex items-center justify-center bg-blue-600"
      >
        <img 
          src={background} 
          alt="Nanthiat Exclusive Investment Advisory" 
          className="w-full h-full object-cover object-center"
        />
      </motion.div>

      {/* Partie droite avec formulaire animé */}
      <motion.div
        initial={{ opacity: 0, x: 100 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 1 }}
        className="w-1/2 flex flex-col justify-center items-center px-10"
      >
        {/* 💡 Ajout d'une boîte qui englobe tout : logo + titre + formulaire */}
        <div className="sm:mx-auto sm:w-full sm:max-w-md bg-white shadow-2xl rounded-lg p-8">
          {/* Logo centré */}
          <div className="flex justify-center">
            <img
              alt="Your Company"
              src={logo}
              className="h-32 w-auto"
            />
          </div>

          {/* Titre */}
          <h2 className="mt-5 text-center text-2xl font-bold tracking-tight text-gray-900">
            Connectez-vous à votre espace
          </h2>

          {/* 🔹 Formulaire de connexion */}
          <form onSubmit={handleLogin} className="space-y-6 mt-8">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-900">
                Adresse Email
              </label>
              <div className="mt-2">
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  autoComplete="email"
                  value={Email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="block w-full rounded-md bg-white px-3 py-2 text-base text-gray-900 shadow-sm border border-gray-300 outline-none focus:ring-2 focus:ring-indigo-500 sm:text-sm"
                />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between">
                <label htmlFor="password" className="block text-sm font-medium text-gray-900">
                  Mot de passe
                </label>
                <div className="text-sm">
                  <button
                    type="button"
                    onClick={() => setOpenForgotPassword(true)}
                    className="font-semibold text-[#21295c] hover:text-[#141937]"
                  >
                    Mot de passe oublié ?
                  </button>
                </div>
              </div>
              <div className="mt-2">
                <input
                  id="password"
                  name="password"
                  type="password"
                  required
                  autoComplete="current-password"
                  value={Password}
                  onChange={(e) => {
                    console.log("🔹 Modification du Password :", e.target.value);
                    setPassword(e.target.value);
                  }}
                  className="block w-full rounded-md bg-white px-3 py-2 text-base text-gray-900 shadow-sm border border-gray-300 outline-none focus:ring-2 focus:ring-indigo-500 sm:text-sm"
                />
              </div>
            </div>

            <div>
              <button
                type="submit"
                className="flex w-full justify-center rounded-md bg-[#21295c] px-3 py-2 text-sm font-semibold text-white shadow-md hover:bg-[#141937] focus:ring-2 focus:ring-[#21295c]"
              >
                Connexion
              </button>
            </div>
          </form>
        </div>
      </motion.div>

      {/* 🔹 Boîte de dialogue pour afficher les erreurs */}
      <Dialog open={open} onClose={() => setOpen(false)} className="relative z-10">
        <DialogBackdrop className="fixed inset-0 bg-gray-500/75" />
        <div className="fixed inset-0 z-10 flex items-center justify-center p-4">
          <DialogPanel className="w-full max-w-md transform overflow-hidden rounded-lg bg-white shadow-xl transition-all">
            <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
              <div className="sm:flex sm:items-start">
                <div className="mx-auto flex size-12 shrink-0 items-center justify-center rounded-full bg-red-100 sm:mx-0 sm:size-10">
                  <ExclamationTriangleIcon aria-hidden="true" className="size-6 text-red-600" />
                </div>
                <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                  <DialogTitle as="h3" className="text-base font-semibold text-gray-900">
                    Erreur de connexion
                  </DialogTitle>
                  <div className="mt-2">
                    <p className="text-sm text-gray-500">{error}</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="inline-flex w-full justify-center rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-xs hover:bg-red-500 sm:ml-3 sm:w-auto"
              >
                OK
              </button>
            </div>
          </DialogPanel>
        </div>
      </Dialog>

      {/* 🔹 Boîte de dialogue "Mot de passe oublié ?" */}
      <Dialog open={openForgotPassword} onClose={() => setOpenForgotPassword(false)} className="relative z-10">
        <DialogBackdrop className="fixed inset-0 bg-gray-500/75" />
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <DialogPanel className="w-full max-w-md bg-white rounded-lg shadow-lg p-6">
            
            {/* 🔹 Icône + Titre alignés */}
            <div className="flex items-center gap-3">
              <QuestionMarkCircleIcon className="w-6 h-6 text-indigo-600" />
              <DialogTitle className="text-lg font-semibold text-gray-900">
                Réinitialisation du mot de passe
              </DialogTitle>
            </div>

            <p className="mt-2 text-sm text-gray-500">
              Veuillez contacter l'administrateur pour obtenir un nouveau mot de passe.
            </p>

            {/* 🔹 Hyperlien avec icône SVG pour ouvrir un email pré-rempli */}
            <p className="mt-4 text-sm flex items-center text-[#21295c] hover:text-[#141937] cursor-pointer">
              <a
                href="mailto:admin@nanthiat.com?subject=Réinitialisation%20des%20identifiants&body=Bonjour,%0D%0A%0D%0AJe%20souhaite%20réinitialiser%20mes%20identifiants%20de%20connexion.%20Merci%20de%20me%20fournir%20un%20nouveau%20mot%20de%20passe.%0D%0A%0D%0ACordialement."
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2"
              >
                <AtSymbolIcon className="w-5 h-5" />
                Réinitialiser mes identifiants
              </a>
            </p>

            {/* Bouton OK pour fermer la boîte de dialogue */}
            <div className="mt-4 flex justify-end">
              <button 
                onClick={() => setOpenForgotPassword(false)} 
                className="px-4 py-2 text-sm font-semibold text-white bg-indigo-600 rounded-md shadow-md hover:bg-indigo-500"
              >
                OK
              </button>
            </div>
          </DialogPanel>
        </div>
      </Dialog>
    </div>
  );
}
